import { useState, useEffect, useCallback } from 'react';

/**
 * A custom event to notify other hooks on the same page about a change in sessionStorage.
 * The native 'storage' event only fires for other tabs/windows, not the one that made the change.
 */
const dispatchStorageEvent = (key: string, newValue: string | null) => {
  window.dispatchEvent(new CustomEvent('session-storage-change', { detail: { key, newValue } }));
};

/**
 * A React hook that syncs state with sessionStorage and makes it "observable".
 * It updates its state when sessionStorage is changed from the same tab (via a custom event)
 * or another tab (via the native 'storage' event).
 *
 * @param key The key for the sessionStorage item.
 * @param initialValue The initial value to use if nothing is in storage.
 * @returns A stateful value, and a function to update it.
 */
export function useObservableSessionStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      //console.error(`Error reading sessionStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== 'undefined') {
        const stringifiedValue = JSON.stringify(valueToStore);
        window.sessionStorage.setItem(key, stringifiedValue);
        dispatchStorageEvent(key, stringifiedValue); // Notify other hooks on the same page
      }
    } catch (error) {
      console.error(`Error setting sessionStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  useEffect(() => {
    // Listener for changes from other tabs
    const handleNativeStorageChange = (event: StorageEvent) => {
      if (event.storageArea === window.sessionStorage && event.key === key) {
        setStoredValue(event.newValue ? JSON.parse(event.newValue) : initialValue);
      }
    };
    // Listener for changes from the same tab
    const handleCustomStorageChange = (event: CustomEvent) => {
      if (event.detail.key === key) {
        setStoredValue(event.detail.newValue ? JSON.parse(event.detail.newValue) : initialValue);
      }
    };

    window.addEventListener('storage', handleNativeStorageChange);
    window.addEventListener('session-storage-change', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleNativeStorageChange);
      window.removeEventListener('session-storage-change', handleCustomStorageChange);
    };
  }, [key, initialValue]);

  return [storedValue, setValue];
}
